import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { getBlogArticleBySlug } from '../../data/blogSeoPages';
import css from './BlogArticlePage.module.css';

const createAbsoluteUrl = (rootUrl, path) => `${(rootUrl || '').replace(/\/$/, '')}${path}`;

// Parses a small subset of markdown — inline links of the form [text](href) — and
// renders them as <a> elements while leaving everything else as plain text. Used
// so article paragraphs can carry natural in-body internal links without storing
// HTML strings or pulling in a full markdown renderer.
const INLINE_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;
const renderInlineLinks = text => {
  if (typeof text !== 'string' || !text) {
    return text;
  }
  if (!text.includes('](')) {
    return text;
  }
  const parts = [];
  let lastIndex = 0;
  let match;
  INLINE_LINK_REGEX.lastIndex = 0;
  while ((match = INLINE_LINK_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const href = match[2];
    const isExternal = /^https?:\/\//i.test(href);
    parts.push(
      isExternal ? (
        <a key={parts.length} href={href} target="_blank" rel="noopener noreferrer">
          {match[1]}
        </a>
      ) : (
        <a key={parts.length} href={href}>
          {match[1]}
        </a>
      )
    );
    lastIndex = INLINE_LINK_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const createBlogSocialSharing = (rootUrl, article) => {
  const imageUrl = createAbsoluteUrl(rootUrl, article.heroImage);
  const image = {
    url: imageUrl,
    width: 1536,
    height: 1024,
    alt: article.heroAlt,
  };

  return {
    title: article.metaTitle,
    description: article.metaDescription,
    twitterCard: 'summary_large_image',
    images1200: [image],
    images600: [image],
  };
};

const buildArticleSchema = (rootUrl, article) => {
  const articleUrl = `${rootUrl}${article.path}`;
  const heroImageUrl = createAbsoluteUrl(rootUrl, article.heroImage);
  const articleImageUrls = Array.from(
    new Set(
      [
        heroImageUrl,
        ...(article.toolsImage ? [createAbsoluteUrl(rootUrl, article.toolsImage.image)] : []),
        ...article.steps
          .filter(step => step.image)
          .map(step => createAbsoluteUrl(rootUrl, step.image)),
      ].filter(Boolean)
    )
  );

  const howToSupply = Array.isArray(article.supplies) && article.supplies.length
    ? article.supplies
    : article.tools;
  const howToTool = Array.isArray(article.howToTools) && article.howToTools.length
    ? article.howToTools
    : article.tools;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.metaDescription,
      image: articleImageUrls,
      datePublished: article.publishedDate,
      dateModified: article.updatedDate,
      mainEntityOfPage: articleUrl,
      author: {
        '@type': 'Organization',
        name: 'FixitTask',
      },
      publisher: {
        '@type': 'Organization',
        name: 'FixitTask',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${rootUrl}/blog`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: articleUrl,
        },
      ],
    },
    // Price guides and other non-instructional articles set skipHowToSchema —
    // emitting HowTo markup for them would misrepresent the content type.
    ...(article.skipHowToSchema
      ? []
      : [
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: article.title,
            description: article.metaDescription,
            image: heroImageUrl,
            totalTime: article.totalTime || 'PT45M',
            supply: howToSupply,
            tool: howToTool,
            step: article.steps.map(step => ({
              '@type': 'HowToStep',
              name: step.title.replace(/^\d+\.\s*/, ''),
              text: step.paragraphs.join(' '),
              image: step.image ? createAbsoluteUrl(rootUrl, step.image) : heroImageUrl,
            })),
          },
        ]),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqs.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];
};

const BlogArticlePage = props => {
  const { params } = props;
  const article = getBlogArticleBySlug(params?.articleSlug);
  const config = useConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';

  if (!article) {
    return <NotFoundPage />;
  }

  const canonicalUrl = `${rootUrl}${article.path}`;
  const schema = buildArticleSchema(rootUrl, article);

  return (
    <Page
      title={article.metaTitle}
      description={article.metaDescription}
      socialSharing={createBlogSocialSharing(rootUrl, article)}
      schema={schema}
      openGraphType="article"
      published={article.publishedDate}
      updated={article.updatedDate}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero}>
            <div className={css.heroCopy}>
              <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                  Home
                </NamedLink>
                <span aria-hidden="true">/</span>
                <NamedLink name="BlogHubPage" className={css.breadcrumbLink}>
                  Blog
                </NamedLink>
                <span aria-hidden="true">/</span>
                <span>{article.pageName}</span>
              </nav>

              <p className={css.eyebrow}>
                {article.eyebrow} <span aria-hidden="true">/</span> {article.readTime}
              </p>
              <h1 className={css.title}>{article.title}</h1>
              <p className={css.lead}>{renderInlineLinks(article.intro)}</p>
            </div>

            <figure className={css.heroMedia}>
              <img
                className={css.heroImage}
                src={article.heroImage}
                alt={article.heroAlt}
                decoding="async"
              />
            </figure>
          </section>

          <div className={css.articleLayout}>
            <aside className={css.sidebar} aria-label="Article contents">
              <p className={css.sidebarTitle}>On this page</p>
              <a href="#quick-answer">Quick answer</a>
              <a href="#tools">{article.toolsTocLabel || 'Tools and parts'}</a>
              <a href="#steps">{article.stepsTocLabel || 'Step-by-step'}</a>
              <a href="#troubleshooting">{article.troubleshootingTocLabel || 'Troubleshooting'}</a>
              {Array.isArray(article.relatedArticles) && article.relatedArticles.length ? (
                <a href="#related">Related guides</a>
              ) : null}
              <a href="#faq">FAQ</a>
            </aside>

            <article className={css.article}>
              <section className={css.quickAnswer} id="quick-answer">
                <p className={css.sectionEyebrow}>Start here</p>
                <h2>{article.quickAnswer.title}</h2>
                <p>{renderInlineLinks(article.quickAnswer.body)}</p>
              </section>

              <section className={css.articleSection} id="tools">
                <p className={css.sectionEyebrow}>
                  {article.toolsEyebrow || 'Before you start'}
                </p>
                <h2>{article.toolsHeading || 'Tools and parts to have nearby'}</h2>
                <p>
                  {renderInlineLinks(
                    article.toolsIntro ||
                      'These repairs are much calmer when everything is within reach. You may not need every item, but having them nearby is what keeps a quick job from turning into a half-finished one with parts spread across the floor.'
                  )}
                </p>
                <ul className={css.checkList}>
                  {article.tools.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {article.toolsImage ? (
                  <figure className={css.inlinePhoto}>
                    <img
                      className={css.inlinePhotoImage}
                      src={article.toolsImage.image}
                      alt={article.toolsImage.imageAlt}
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption>{article.toolsImage.caption}</figcaption>
                  </figure>
                ) : null}
              </section>

              <section className={css.articleSection}>
                <p className={css.sectionEyebrow}>
                  {article.beforeYouStartEyebrow || 'A little diagnosis first'}
                </p>
                <h2>
                  {article.beforeYouStartHeading || 'What to check before you begin'}
                </h2>
                <div className={css.noteGrid}>
                  {article.beforeYouStart.map(item => (
                    <div className={css.noteCard} key={item.title}>
                      <h3>{item.title}</h3>
                      <p>{renderInlineLinks(item.body)}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className={css.articleSection} id="steps">
                <p className={css.sectionEyebrow}>
                  {article.stepsEyebrow || 'Step-by-step'}
                </p>
                <h2>{article.stepsHeading || article.title}</h2>
                <div className={css.steps}>
                  {article.steps.map(step => (
                    <section
                      className={step.image ? css.stepCard : css.stepCardNoImage}
                      key={step.title}
                    >
                      {step.image ? (
                        <figure className={css.stepImageWrap}>
                          <img
                            className={css.stepImage}
                            src={step.image}
                            alt={step.imageAlt}
                            loading="lazy"
                            decoding="async"
                          />
                        </figure>
                      ) : null}
                      <div className={css.stepCopy}>
                        <h3>{step.title}</h3>
                        {step.paragraphs.map(paragraph => (
                          <p key={paragraph}>{renderInlineLinks(paragraph)}</p>
                        ))}
                        {step.note ? (
                          <aside className={css.stepNote}>
                            <h4>{step.note.title}</h4>
                            <p>{renderInlineLinks(step.note.body)}</p>
                          </aside>
                        ) : null}
                      </div>
                    </section>
                  ))}
                </div>
              </section>

              <section className={css.articleSection} id="troubleshooting">
                <p className={css.sectionEyebrow}>
                  {article.troubleshootingEyebrow || 'If something still feels off'}
                </p>
                <h2>
                  {article.troubleshootingHeading || 'Troubleshooting common problems'}
                </h2>
                <div className={css.troubleshootingGrid}>
                  {article.troubleshooting.map(item => (
                    <div className={css.troubleshootingCard} key={item.problem}>
                      <h3>{item.problem}</h3>
                      <p>{renderInlineLinks(item.solution)}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className={css.articleSection}>
                <p className={css.sectionEyebrow}>
                  {article.whenToCallHelpEyebrow || 'Know when to stop'}
                </p>
                <h2>
                  {article.whenToCallHelpHeading || 'When it is better to call for help'}
                </h2>
                <p>
                  {renderInlineLinks(
                    article.whenToCallHelpIntro ||
                      'There is no prize for turning a small repair into a much larger one. If one of these situations shows up, bringing in help is the practical move:'
                  )}
                </p>
                <ul className={css.checkList}>
                  {article.whenToCallHelp.map(item => (
                    <li key={item}>{renderInlineLinks(item)}</li>
                  ))}
                </ul>
              </section>

              <section className={css.ctaCard}>
                <div>
                  <p className={css.sectionEyebrow}>FixitTask</p>
                  <h2>{article.cta.title}</h2>
                  <p>{renderInlineLinks(article.cta.body)}</p>
                </div>
                <a className={css.primaryButton} href={article.cta.href}>
                  {article.cta.buttonLabel}
                </a>
              </section>

              {Array.isArray(article.relatedArticles) && article.relatedArticles.length ? (
                <section className={css.articleSection} id="related">
                  <p className={css.sectionEyebrow}>Keep going</p>
                  <h2>Related guides on FixitTask</h2>
                  <ul className={css.checkList}>
                    {article.relatedArticles.map(item => (
                      <li key={item.href}>
                        <a href={item.href}>{item.label}</a>
                        {item.description ? <> — {item.description}</> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section className={css.articleSection} id="faq">
                <p className={css.sectionEyebrow}>FAQ</p>
                <h2>{article.faqHeading || 'Questions people usually ask'}</h2>
                <div className={css.faqGrid}>
                  {article.faqs.map(item => (
                    <div className={css.faqCard} key={item.question}>
                      <h3>{item.question}</h3>
                      <p>{renderInlineLinks(item.answer)}</p>
                    </div>
                  ))}
                </div>
              </section>
            </article>
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

BlogArticlePage.propTypes = {
  params: PropTypes.shape({
    articleSlug: PropTypes.string,
  }).isRequired,
};

export default BlogArticlePage;
